import PodcastCollection from 'api/models/PodcastCollection';
import PromotionCollection from 'api/models/PromotionCollection';
import ShowCollection from 'api/models/ShowCollection';
import StationCollection from 'api/models/StationCollection';
import TagCollection from 'api/models/TagCollection';
import MixedCollection from 'api/models/MixedCollection';
import optimizely from 'api/optimizely';
import { parseConfig } from 'util/index';
import CONTENT_TYPES from 'globalConst/contentTypes-const';
import { UniversalCollection, UniversalConfig, UniversalSection } from 'api/interfaces';
import Collection from 'api/models/Collection';

/**
 * Create models of the raw content
 * @param {UniversalCollection} collection
 * @param {string} type
 * @param {object} config
 * @param {string[]} options
 * @returns {object} StationCollection | ShowCollection | PodcastCollection | TagCollection | PromotionCollection
 */
export const parseContentModal = (
  collection: UniversalCollection,
  type: string,
  config: UniversalConfig[],
  options: string[]
): Collection | MixedCollection | null => {
  if (!collection || !type) {
    return;
  }

  if (type === CONTENT_TYPES.STATION_COLLECTION) {
    return new StationCollection(collection, config, options);
  }

  if (type === CONTENT_TYPES.SHOW_COLLECTION) {
    return new ShowCollection(collection, config, options);
  }

  if (type === CONTENT_TYPES.PODCAST_COLLECTION) {
    return new PodcastCollection(collection, config, options);
  }

  if (type === CONTENT_TYPES.TAG_COLLECTION) {
    return new TagCollection(collection, config, options);
  }

  if (type === CONTENT_TYPES.PROMOTION_COLLECTION) {
    return new PromotionCollection(collection, config, options);
  }

  if (type === CONTENT_TYPES.MIXED_COLLECTION) {
    return new MixedCollection(collection);
  }

  return null;
};

/**
 * Let optimizely determine what variant the use should get and return the slug of that variant
 *
 * @param {{ references: [Collection] }} section - section with references of collection
 * @param {string} experiment - experiment name to check for inside collection config variant key
 * @returns {object} experiment collection slug or first one as default
 */
export const parseExperimentVariant = (section: UniversalSection, experiment: string): UniversalCollection => {
  const optimizelyVariant = optimizely.activate(experiment);

  // By default use first collection as variant
  let variant = section.references[0];

  // Check if there is optimizely variant inside collection config
  // Search for:
  //    collection.config.VariationKey = 'optimizely_variant_name'
  section.references.some((collection) => {
    const variantConfig = parseConfig(collection.config);
    const isOptimizelyVariant = optimizelyVariant === variantConfig.VariationKey;
    if (isOptimizelyVariant) {
      variant = collection;
    }

    return isOptimizelyVariant;
  });

  return variant;
};
